<template>
  <div :class="{ 'task-detail-chronology--has-tabs': hasActions }" class="task-detail-chronology">
    <chronology
      :items="renderItems"
      :comment-submit-handler="commentSubmitHandler"
      :is-for-task="true"
    />
    <div v-if="hasHistory" class="dd-divider" />
    <comment-or-reply
      :entity-id="projectId"
      :item-id="taskId"
      :is-submitting="isSubmitting"
      :is-traffic-cop-checking="isTrafficCopChecking"
      :is-post-submit-pre-refetch-data="isPostSubmitPreRefetchData"
      :author="profile"
      :mention-users="mentionUsers"
      :responders-since-last-view="respondersSinceLastView"
      :assignee-id="assigneeId"
      :assignees-options="assigneesOptions"
      :priority-id="priorityId"
      :priority-options="priorityOptions"
      :status-id="statusId"
      :status-options="statusOptions"
      :due-date="dueDate as string"
      :watcher-ids="listWatchers"
      :watchers-options="watchersOptions"
      :job-info="jobInfo"
      :type="EEntities.PROJECTS"
      :has-guest-users="hasGuestUsers"
      class="task-detail-chronology__comment marker--task-detail-chronology--comment"
      @editor-opened="(val) => $emit('editor-opened', val)"
      @update-watchers="(newVal) => $emit('update-watchers', newVal)"
      @submit="(formData) => $emit('submit', formData)"
      @confirmed-traffic-cop-note="$emit('confirmed-traffic-cop-note')"
    />
  </div>
</template>
<script lang="ts">
/* General */
import { defineComponent } from 'vue'

/* Mixins */

/* Components */

/* Helpers */

/* Data types */
import { EEntities } from '@/types'
import {
  IChronologyItem,
  IChronologyItemTypes,
} from '@/types/data-objects/item-histories'
import { IProfile } from '@/types/data-objects/profile'
import {
  IAtMentionUser,
  IItemResponder,
  IPersonWithEmail,
  IPriorityLevelForSelection,
  IStatusForSelection,
} from '@/api/types/response.types'
import { IJobInfo } from '@/types/data-objects/items'

/* API */

export default defineComponent({
  name: 'TaskDetailChronology',

  props: {
    projectId: {
      type: Number,
      required: true,
    },

    taskId: {
      type: Number,
      required: true,
    },

    items: {
      type: Array as () => IChronologyItem[],
      required: true,
    },

    isSubmitting: {
      type: Boolean,
      default: false,
    },

    isTrafficCopChecking: {
      type: Boolean,
      default: false,
    },

    isPostSubmitPreRefetchData: {
      type: Boolean,
      default: false,
    },

    /* Editor */
    mentionUsers: {
      type: Array as () => IAtMentionUser[],
      required: true,
    },

    /* Assignee picker */
    assigneeId: {
      type: Number,
      default: null,
    },

    /* Priority */
    priorityId: {
      type: Number,
      required: true,
    },

    priorityOptions: {
      type: Array as () => IPriorityLevelForSelection[],
      required: true,
    },

    /* Status */
    statusId: {
      type: Number,
      required: true,
    },

    statusOptions: {
      type: Array as () => IStatusForSelection[],
      required: true,
    },

    respondersSinceLastView: {
      type: Array as () => IItemResponder[],
      default: (): IItemResponder[] => [],
    },

    /* Due date */
    dueDate: {
      type: [Date, String],
      default: null,
    },

    /* Watchers picker */
    listWatchers: {
      type: Array as () => number[],
      required: true,
    },

    assigneesOptions: {
      type: Array as () => IPersonWithEmail[],
      required: true,
    },

    watchersOptions: {
      type: Array as () => IPersonWithEmail[],
      required: true,
    },

    profile: {
      type: Object as () => IProfile,
      required: true,
    },

    jobInfo: {
      type: Object as () => IJobInfo,
      default: null,
    },

    commentSubmitHandler: {
      type: Function,
      default: (): any => {},
    },

    hasGuestUsers: {
      type: Boolean,
      default: false,
    },
  },

  emits: [
    'confirmed-traffic-cop-note',
    'editor-opened',
    'submit',
    'update-watchers',
  ],

  data() {
    return {
      renderItems: [] as any[],
      EEntities,
    }
  },

  computed: {
    hasHistory(): boolean {
      return this.renderItems && this.renderItems.length > 0
    },
    hasActions(): boolean {
      return this.renderItems.some(i => i.type === IChronologyItemTypes.ACTION)
    },
  },

  watch: {
    items: {
      deep: 1,
      handler() {
        this.renderItems = this.items
      },
    },
  },

  mounted() {
    this.renderItems = this.items
  },
})
</script>

<style lang="scss">
.task-detail-chronology {
  background-color: var(--color-background-primary);
  border-top: $border--solid-light;
  // Mimicking bottom curve of parent el-card
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;

  &--has-tabs {
    border-top: none;
  }

  &__comment {
    margin-top: 20px;

    &.comment-or-reply {
      padding: 24px $detail-gutter-pad-sm;

      @include media-breakpoint-up(md) {
        padding: 24px $detail-gutter-pad-md 64px $detail-gutter-pad-md;
      }

      @include media-breakpoint-up(lg) {
        padding: 24px $detail-gutter-pad-lg 64px $detail-gutter-pad-lg;
      }
    }
  }
}
</style>
